import {
  type EnhancedDimension,
  type MediumIcon,
  dimensions as gammaDimensions,
} from '@factorialco/deprecated-design-system'
import { IconType } from '@factorialco/f0-react'
import { Person, Pin } from '@factorialco/f0-react/icons/app'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { dateToString, formatBackendDateToMonthYear } from 'modules/core/lib/dates'
import type { FormatTypes } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

export type ViewDimension = EnhancedDimension | 's170'

export type RouteOption =
  | 'daily/employees'
  | 'weekly/employees'
  | 'four-weeks/employees'
  | 'monthly/employees'
  | 'weekly/areas'

export interface ViewByGroup {
  view: ViewType
  enabled: () => boolean
}

export interface GroupOption {
  group: GroupType
  enabled: () => boolean
  icon: MediumIcon
  newThemeIcon: IconType
  children: ViewByGroup[]
}

const views = ['daily', 'weekly', 'four-weeks', 'monthly'] as const
const granularityViews = ['day', 'week', 'month'] as const
const groups = ['employees', 'areas'] as const

export type ViewType = (typeof views)[number]
export type GranularityViewType = (typeof granularityViews)[number]
export type GroupType = (typeof groups)[number]

export const isValidViewType = (value: string | undefined): value is ViewType => {
  return views.includes(value as ViewType)
}

export const isValidGranularityViewType = (
  value: string | undefined
): value is GranularityViewType => {
  return granularityViews.some((v) => v === value)
}

export const transformGranularityViewTypeToViewType = (value: GranularityViewType): ViewType => {
  return value === 'day' ? 'daily' : value === 'week' ? 'weekly' : 'monthly'
}

export const isValidGroupType = (value: string | undefined): value is GroupType => {
  return groups.includes(value as GroupType)
}

export const DEFAULT_VIEW_TYPE: ViewType = 'weekly'
export const DEFAULT_GROUP_TYPE: GroupType = 'employees'

// WARNING! This is the source of truth in order to enable a view or a group
// updating this constant, we are updating the following:
// - Allowed views by flags
// - Visibility and Order of options in the view selector
export const ALLOWED_VIEWS_BY_GROUP: GroupOption[] = [
  {
    group: 'employees',
    enabled: () => true,
    icon: 'person',
    newThemeIcon: Person,
    children: [
      {
        view: 'daily',
        enabled: () => hasFeature('shift_management_daily_view'),
      },
      {
        view: 'weekly',
        enabled: () => true,
      },
      {
        view: 'four-weeks',
        enabled: () => hasFeature('shift_management_monthly_view'),
      },
      {
        view: 'monthly',
        enabled: () => hasFeature('shift_management_monthly_view'),
      },
    ],
  },
  {
    group: 'areas',
    enabled: () => hasFeature('shift_management_area_view'),
    icon: 'space-control',
    newThemeIcon: Pin,
    children: [
      {
        view: 'weekly',
        enabled: () => true,
      },
    ],
  },
]

// Override Gamma's dimensions because gamma does not have s170 as a dimension size
// which is necessary to accommodate the new cell design
export const overrideGammaDimensions = (): Record<ViewDimension, string> => {
  return {
    ...gammaDimensions,
    s170: '170px',
    full: 'full',
    none: 'none',
    'min-content': 'min-content',
    'fit-content': 'fit-content',
    'full-view-height': 'full-view-height',
  }
}

interface Props {
  date: moment.Moment
  viewType?: ViewType | undefined
  groupType?: GroupType | undefined
  showCoverageSection?: boolean
  isTimeFormat24H: boolean
}

class View {
  date: moment.Moment
  viewType: ViewType
  groupType: GroupType
  showCoverageSection: boolean
  isTimeFormat24H: boolean

  constructor({ date, viewType, groupType, isTimeFormat24H }: Props) {
    this.date = date

    this.isTimeFormat24H = isTimeFormat24H

    this.groupType = isValidGroupType(groupType) ? groupType : DEFAULT_GROUP_TYPE

    const isViewAllowed = viewType && this.allowedViews[this.groupType]?.has(viewType)

    this.viewType = isViewAllowed ? viewType : DEFAULT_VIEW_TYPE

    this.showCoverageSection = false

    makeObservable(this, {
      allowedGroups: computed,
      allowedViews: computed,
      granularity: computed,
      isDaily: computed,
      isWeekly: computed,
      isMonthly: computed,
      isFourWeeks: computed,
      isByEmployees: computed,
      isByAreas: computed,
      isFullVersion: computed,
      columnUnit: computed,
      period: computed,
      dates: computed,
      firstWeekNumber: computed,
      lastWeekNumber: computed,
      startAt: computed,
      endAt: computed,
      firstDay: computed,
      lastDay: computed,
      previousPeriod: computed,
      nextPeriod: computed,
      formattedStartRange: computed,
      formattedEndRange: computed,
      formattedMonthYear: computed,
      formattedDay: computed,
      startRangeYear: computed,
      endRangeYear: computed,
      formattedStartMonthOnly: computed,
      displayRange: computed,
      headerTitle: computed,
      columnMinWidth: computed,
      cellMaxWidth: computed,
      cellMinHeight: computed,
      dateFormat: computed,
      currentDate: computed,
      currentDateString: computed,
      weeksInPeriod: computed,
    })
  }

  // Returns Set('employees', 'areas')
  get allowedGroups(): Set<string> {
    return new Set(
      ALLOWED_VIEWS_BY_GROUP.reduce<string[]>((result, { group, enabled }) => {
        if (enabled()) result.push(group)
        return result
      }, [])
    )
  }

  // Returns {'employees': Set('weekly', 'monthly'), 'areas': Set(...)
  get allowedViews(): Record<string, Set<string>> {
    return ALLOWED_VIEWS_BY_GROUP.reduce<Record<string, Set<string>>>(
      (result, { group, enabled, children }) => {
        if (enabled()) {
          const viewGroups = children.reduce<string[]>((acc, { view, enabled }) => {
            if (enabled()) acc.push(view)
            return acc
          }, [])

          result[group] = new Set(viewGroups)
        }
        return result
      },
      {}
    )
  }

  get isDaily(): boolean {
    return this.viewType === 'daily'
  }

  get isWeekly(): boolean {
    return this.viewType === 'weekly'
  }

  get isMonthly(): boolean {
    return this.viewType === 'monthly'
  }

  get isFourWeeks(): boolean {
    return this.viewType === 'four-weeks'
  }

  get granularity(): GranularityViewType {
    if (this.isDaily) return 'day'
    if (this.isWeekly || this.isFourWeeks) return 'week'

    return 'month'
  }

  get isByEmployees(): boolean {
    return this.groupType === 'employees'
  }

  get isByAreas(): boolean {
    return this.groupType === 'areas'
  }

  get isFullVersion(): boolean {
    return this.isDaily || this.isWeekly
  }

  get columnUnit(): moment.unitOfTime.DurationConstructor {
    return this.isDaily ? 'hour' : 'day'
  }

  get period(): moment.unitOfTime.DurationConstructor {
    if (this.isDaily) return 'day'
    if (this.isMonthly) return 'month'

    return 'week'
  }

  get dates(): moment.Moment[] {
    const days: moment.Moment[] = []
    let day = this.firstDay
    const seenHourKeys = new Set<string>()

    while (day <= this.lastDay) {
      if (this.columnUnit === 'hour') {
        const hourKey = day.format('YYYY-MM-DD-HH')
        // During DST fall back, the same hour appears twice (e.g., 2am DST and 2am STD)
        // Skip duplicates to prevent showing the same hour twice
        if (seenHourKeys.has(hourKey)) {
          day = day.clone().add(1, 'hour')
          continue
        }
        seenHourKeys.add(hourKey)
      }

      days.push(day)
      day = day.clone().add(1, this.columnUnit)
    }

    return days
  }

  get firstWeekNumber(): number {
    return this.firstDay.week()
  }

  get lastWeekNumber(): number {
    return this.lastDay.week()
  }

  get firstDay(): moment.Moment {
    if (this.isDaily) return this.date.clone().startOf('day')
    if (this.isMonthly) return this.date.clone().startOf('month')

    return this.date.clone().startOf('week')
  }

  get lastDay(): moment.Moment {
    if (this.isDaily) return this.date.clone().endOf('day')
    if (this.isMonthly) return this.date.clone().endOf('month')
    if (this.isFourWeeks) return this.date.clone().endOf('week').add(3, 'weeks')

    return this.date.clone().endOf('week')
  }

  get startAt(): string {
    return dateToString(this.firstDay, 'backend')
  }

  get endAt(): string {
    return dateToString(this.lastDay, 'backend')
  }

  get weeksInPeriod(): { startAt: string; endAt: string }[] {
    if (this.isWeekly) return [{ startAt: this.startAt, endAt: this.endAt }]

    if (this.isFourWeeks) {
      const weeks = Array.from({ length: 4 }, (_, i) => {
        const start = this.firstDay.clone().add(i, 'weeks')
        return {
          startAt: start.format('YYYY-MM-DD'),
          endAt: start.clone().endOf('week').format('YYYY-MM-DD'),
        }
      })

      return weeks
    }

    // Monthly view
    const start = moment(this.firstDay)
    const end = moment(this.lastDay)
    const weeks: { startAt: string; endAt: string }[] = []

    const currentWeekStart = start.clone().startOf('week')

    while (currentWeekStart.isSameOrBefore(end)) {
      const currentWeekEnd = currentWeekStart.clone().endOf('week')

      if (currentWeekEnd.isSameOrAfter(start)) {
        weeks.push({
          startAt: currentWeekStart.format('YYYY-MM-DD'),
          endAt: currentWeekEnd.format('YYYY-MM-DD'),
        })
      }
      currentWeekStart.add(1, 'week')
    }

    return weeks
  }

  get previousPeriod(): View {
    if (this.isDaily) return new View({ ...this, date: this.date.clone().subtract(1, 'day') })
    if (this.isMonthly) return new View({ ...this, date: this.date.clone().subtract(1, 'month') })
    if (this.isFourWeeks) return new View({ ...this, date: this.date.clone().subtract(28, 'days') })

    return new View({ ...this, date: this.date.clone().subtract(7, 'days') })
  }

  get nextPeriod(): View {
    if (this.isDaily) return new View({ ...this, date: this.date.clone().add(1, 'day') })
    if (this.isMonthly) return new View({ ...this, date: this.date.clone().add(1, 'month') })
    if (this.isFourWeeks) return new View({ ...this, date: this.date.clone().add(28, 'days') })

    return new View({ ...this, date: this.date.clone().add(7, 'days') })
  }

  specificPeriod(start: moment.Moment): View {
    if (this.isDaily) return new View({ ...this, date: start })
    if (this.isMonthly) return new View({ ...this, date: start.startOf('month') })
    if (this.isFourWeeks) return new View({ ...this, date: start.startOf('week') })

    return new View({ ...this, date: start.startOf('week') })
  }

  get formattedStartRange(): string {
    return formatBackendDateToMonthYear(this.firstDay)
  }

  get formattedEndRange(): string {
    return formatBackendDateToMonthYear(this.lastDay)
  }

  get formattedMonthYear(): string {
    return this.firstDay.format('MMMM YYYY')
  }

  get formattedDay(): string {
    return this.firstDay.format('dddd, LL')
  }

  get startRangeYear(): number {
    return this.firstDay.year()
  }

  get startRangeMonth(): number {
    return this.firstDay.month() + 1
  }

  get startRangeDay(): number {
    return this.firstDay.date()
  }

  get endRangeYear(): number {
    return this.lastDay.year()
  }

  get formattedStartMonthOnly(): string {
    return this.firstDay.format('MMM')
  }

  get displayRange(): string {
    if (this.formattedStartRange === this.formattedEndRange) {
      return this.formattedMonthYear
    }

    if (this.startRangeYear === this.endRangeYear) {
      return this.formattedStartMonthOnly?.concat(' - ', this.formattedEndRange)
    }

    return this.formattedStartRange.concat(' - ', this.formattedEndRange)
  }

  get headerTitle(): string {
    if (this.isDaily) return this.formattedDay
    if (this.isMonthly) return this.displayRange

    if (this.isFourWeeks) {
      const weeks = `${this.firstWeekNumber}-${this.lastWeekNumber}`
      return `${i18n.t('formatter.week')} ${weeks}: ${this.displayRange}`
    }

    return `${i18n.t('formatter.week')} ${this.firstWeekNumber}: ${this.displayRange}`
  }

  get columnMinWidth(): ViewDimension {
    if (!this.isWeekly) return this.isTimeFormat24H ? 's64' : 's80'

    return this.isTimeFormat24H ? 's140' : 's170'
  }

  get cellMaxWidth(): ViewDimension {
    if (this.isDaily) return 'none'
    if (!this.isWeekly) return this.isTimeFormat24H ? 's64' : 's80'

    return this.isTimeFormat24H ? 's140' : 's170'
  }

  get cellMinHeight(): EnhancedDimension {
    return 's32'
  }

  get cellMaxHeight(): EnhancedDimension {
    return 's48'
  }

  // Returns a different format to consider a datetime or only the date depending on the view
  get dateFormat(): FormatTypes {
    return this.isDaily ? 'ui_short_with_time' : 'backend'
  }

  get currentDate(): moment.Moment {
    return this.date.isSame(moment(), this.period) ? moment() : this.firstDay
  }

  get currentDateString(): string {
    return dateToString(this.currentDate, 'backend')
  }

  isDateFirstDayOfPeriod(date: moment.Moment): boolean {
    const formattedDate = dateToString(date, this.dateFormat)
    return formattedDate === dateToString(this.firstDay, this.dateFormat)
  }
}

export default View
